import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { isDefined } from '@sgme/fp';

import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import type { ValidateOrderResponse } from '@/store/orders/state.ts';
import type { ExpiryDate, GoodTillCancelled } from '@/store/orders/takeProfitOrder/state.ts';
import type { ClientWay } from '@/store/orders/types.ts';
import type { ISODate, StringTime } from '@/types/date.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import type { Timestamp } from '@/utils/date.ts';
import { toBase64 } from '@/utils/toBase64.ts';

export type ValidateTakeProfitRequest = {
  tileId: string;

  currency1: string;
  currency2: string;
  clientId: number;
  way: ClientWay;
  notional?: number;
  notionalCurrencyIndex: CurrencyIndex; // 1 for ccy1, 2 for ccy2
  limitPrice?: number;
  customerPrice?: number;
  margin?: number;
  expiry: GoodTillCancelled | ExpiryDate;
  notificationEmails: {
    emails: string[];
    isOverridenEmails: boolean; // true if the emails are overridden by the user, false if they are from user preferences
  };
  localTimeWithOffSet: Timestamp;
};

export type ValidateTakeProfitBody = {
  ccyPair: string;
  bdrId: string;
  way: ClientWay;
  amountInCcy1?: number;
  amountInCcy2?: number;
  limitPrice?: string;
  customerPrice?: string;
  margin?: number;
  isGtc: boolean;
  expiryDay?: ISODate; // "2025-06-11"
  expiryTime?: StringTime; // "08:13:44"
  base64Emails: string; // base64 encoded emails
  localTimeWithOffSet: Timestamp;
};

export const validateTakeProfit = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<ValidateOrderResponse, ValidateTakeProfitRequest>({
    query: (request) => ({
      url: '/oms/validate/TakeProfit',
      method: 'POST',
      body: generateBody(request),
      timeout: 1000,
      headers: {
        // without this header, the response is a string
        // instead of JSON, even if the value is "application/json"
        accept: '*/*',
      },
    }),
    transformErrorResponse: (errorResponse): ValidateOrderResponse | FetchBaseQueryError => {
      if (errorResponse.status === 400) {
        if (typeof errorResponse.data === 'string') {
          const data = JSON.parse(errorResponse.data);

          if (typeof data === 'object' && 'validationResponseDetailContent' in data) {
            return data as ValidateOrderResponse;
          }
        }

        return errorResponse.data as ValidateOrderResponse;
      }

      return errorResponse;
    },
  });
};

const generateBody = (request: ValidateTakeProfitRequest): ValidateTakeProfitBody => ({
  ccyPair: `${request.currency1}/${request.currency2}`,
  bdrId: String(request.clientId),
  way: request.way,
  amountInCcy1: request.notionalCurrencyIndex === 1 ? request.notional : undefined,
  amountInCcy2: request.notionalCurrencyIndex === 2 ? request.notional : undefined,
  limitPrice: isDefined(request.limitPrice) ? String(request.limitPrice) : undefined,
  customerPrice: isDefined(request.customerPrice) ? String(request.customerPrice) : undefined,
  margin: request.margin ?? undefined,
  isGtc: request.expiry.type === 'GoodTillCancelled',
  expiryDay: request.expiry.type === 'ExpiryDate' ? request.expiry.date : undefined, // ISODate
  expiryTime: request.expiry.type === 'ExpiryDate' ? request.expiry.time : undefined, // StringTime
  base64Emails: isDefined(request.notificationEmails.emails)
    ? toBase64(request.notificationEmails.emails.join(';'))
    : '', // base64 encode the emails
  localTimeWithOffSet: request.localTimeWithOffSet,
});
